import type {
  BodyMetric,
  CyclePlan,
  Exercise,
  ExerciseTrend,
  MuscleGroup,
  RecentRecord,
  PlanLine,
  PrescriptionPatch,
  Session,
  SessionDetail,
  SetEntry,
  SetEntryInput,
  TrendRange,
  SessionWearable,
  WearableDay,
} from "@training/shared";
import type { MeasureField } from "./body.ts";
import * as queue from "./queue.ts";

export class OfflineError extends Error {
  constructor() {
    super("offline");
    this.name = "OfflineError";
  }
}

/**
 * The session cookie is missing or expired. Not the payload's fault: whatever
 * was being written must wait for the next sign-in, never be dropped.
 */
export class NotSignedInError extends Error {
  constructor() {
    super("Connexion requise.");
    this.name = "NotSignedInError";
  }
}

/** Writes that will go through later as they are: parked in the queue, not failed. */
const canWait = (error: unknown) => error instanceof OfflineError || error instanceof NotSignedInError;

let onSignedOut: () => void = () => undefined;

/** What to do when the server says the device is signed out — the app sends it to the sign-in screen. */
export function whenSignedOut(handler: () => void): void {
  onSignedOut = handler;
}

/** A response the server refused, with the status so callers can tell apart why. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Requests are short-lived on purpose: a hanging fetch mid-set is a bug. */
async function request<T>(path: string, init?: RequestInit, timeoutMs = 6000): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`/api${path}`, {
      ...init,
      signal: controller.signal,
      headers: {
        // Only when something is actually being sent: Fastify rejects an empty
        // body that claims to be JSON, which made every DELETE fail with a 400.
        ...(init?.body === undefined || init?.body === null ? {} : { "content-type": "application/json" }),
        ...(init?.headers ?? {}),
      },
    });
    if (response.status === 401 && path !== "/auth/login") {
      onSignedOut();
      throw new NotSignedInError();
    }
    if (!response.ok) {
      const body = await response.text();
      let code: string | null = null;
      let message = body.slice(0, 200);
      try {
        const parsed = JSON.parse(body) as { error?: string; message?: string };
        code = parsed.error ?? null;
        message = parsed.message ?? parsed.error ?? message;
      } catch {
        // Not JSON — keep the raw text.
      }
      throw new ApiError(response.status, code, message);
    }
    // An empty body is a success with nothing to say, whatever the status.
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw new OfflineError();
    if (error instanceof TypeError) throw new OfflineError(); // network unreachable
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

/* ---- account ------------------------------------------------------ */

export interface Account {
  username: string;
  role: "athlete" | "coach";
  firstName: string | null;
  lastName: string | null;
  /** When the picture last changed; null without one. */
  avatarAt: string | null;
  /** The square cut from the kept photo; null when there is no photo to reframe. */
  avatarCrop: AvatarCrop | null;
}

/** A square in the kept photo's pixels. */
export interface AvatarCrop {
  x: number;
  y: number;
  size: number;
}

export const signIn = (username: string, password: string) =>
  request<{ user: Account }>("/auth/login", { method: "POST", body: JSON.stringify({ username, password }) });
export const signOut = () => request<void>("/auth/logout", { method: "POST" });

/** A look at the home screen; `since` is when the previous visit ended, null on the first. */
export const recordVisit = () => request<{ since: string | null }>("/visits", { method: "POST" });

export const updateProfile = (firstName: string | null, lastName: string | null) =>
  request<void>("/profile", { method: "PUT", body: JSON.stringify({ firstName, lastName }) });
/**
 * The square, where it was cut, and the photo when it is a new one: in one
 * request, so they are kept together or not at all. Without the photo, the
 * one already kept is reframed.
 */
export const saveAvatar = async (avatar: Blob, crop: AvatarCrop, photo: Blob | null) =>
  request<void>(
    "/profile/avatar",
    {
      method: "PUT",
      body: JSON.stringify({
        avatar: await toBase64(avatar),
        crop,
        ...(photo ? { source: await toBase64(photo) } : {}),
      }),
    },
    20_000,
  );
export const removeAvatar = () => request<void>("/profile/avatar", { method: "DELETE" });
/** A new picture is a new address: the old one can stay cached for good. */
export const avatarUrl = (account: Account) =>
  account.avatarAt ? `/api/profile/avatar?v=${encodeURIComponent(account.avatarAt)}` : null;
export const avatarPhotoUrl = (account: Account) =>
  account.avatarAt && account.avatarCrop
    ? `/api/profile/avatar/source?v=${encodeURIComponent(account.avatarAt)}`
    : null;

/** A blob as base64, without its data: prefix. */
async function toBase64(blob: Blob): Promise<string> {
  const url = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("Lecture de la photo impossible."));
    reader.readAsDataURL(blob);
  });
  return url.slice(url.indexOf(",") + 1);
}
export const getAccount = () => request<{ user: Account }>("/auth/me");

/* ---- reads -------------------------------------------------------- */

export const getCurrentSession = () => request<{ session: Session | null }>("/sessions/current");
export const getSessions = (params: { from?: string; to?: string; limit?: number } = {}) =>
  request<Session[]>(`/sessions?${new URLSearchParams(params as Record<string, string>)}`);
export const getSessionDetail = (id: number) => request<SessionDetail>(`/sessions/${id}`);
export const getExercises = () => request<Exercise[]>("/exercises");
export const getExerciseTrend = (id: number, range: TrendRange) =>
  request<ExerciseTrend>(`/exercises/${id}/trend?range=${range}`);
/** `templateId` narrows to one of the cycle's sessions; omitted, every one. */
const forTemplate = (templateId?: number | null) => (templateId ? `template=${templateId}` : "");
export const getTrackedExercises = (templateId?: number | null) =>
  request<TrackedExercise[]>(`/exercises/tracked?${forTemplate(templateId)}`);
export const getProgressGrid = (templateId: number) => request<ProgressGrid>(`/cycle/templates/${templateId}/progress`);
export const getOverview = (range: TrendRange, templateId?: number | null) =>
  request<Overview>(`/overview?range=${range}&${forTemplate(templateId)}`);
/** The records of the last week, latest first. */
export const getRecentRecords = () => request<{ days: number; records: RecentRecord[] }>("/records/recent");
export const getSyncStatus = () => request<SyncStatus>("/sync/status");
export const getSettings = () =>
  request<{ plateIncrementKg: number; defaultRestSeconds: number; sheetsConfigured: boolean }>("/settings");
export const getCurrentBlock = () => request<{ block: Block | null }>("/blocks/current");
export const getCycle = () => request<{ cycle: Cycle | null }>("/cycle");

/** Copy one of the cycle's templates into a dated session and open it. */
export const startTemplate = (templateId: number) =>
  request<{ session: Session }>(`/cycle/templates/${templateId}/start`, { method: "POST", body: "{}" });

/** Rename a session. Sticks across pulls, unlike the coach's split line. */
export const renameTemplate = (templateId: number, name: string | null) =>
  request<{ template: CycleTemplate }>(`/cycle/templates/${templateId}`, {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });

/** Rename a movement in the app; the sheet keeps the coach's wording. Null restores it. */
export const renameExercise = (exerciseId: number, name: string | null) =>
  request<Exercise>(`/exercises/${exerciseId}`, { method: "PATCH", body: JSON.stringify({ name }) });

/** Whether a movement is logged one side at a time, left then right. */
export const setExerciseUnilateral = (exerciseId: number, isUnilateral: boolean) =>
  request<Exercise>(`/exercises/${exerciseId}`, { method: "PATCH", body: JSON.stringify({ isUnilateral }) });

/** What a movement trains: its target, and the muscles that work along. Sent together, the second hangs on the first. */
export const setExerciseMuscles = (
  exerciseId: number,
  muscleGroup: MuscleGroup | null,
  secondaryMuscles: MuscleGroup[],
) =>
  request<Exercise>(`/exercises/${exerciseId}`, {
    method: "PATCH",
    body: JSON.stringify({ muscleGroup, secondaryMuscles }),
  });

/** The cycle's sessions line by line: the coach's lines under what was changed here. */
export const getPlan = () => request<{ plan: CyclePlan | null }>("/cycle/plan");

/**
 * Change what a movement asks for in one session of the cycle. Sticks across
 * pulls; a field sent back at the sheet's value follows the coach again.
 */
export const patchPrescription = (templateId: number, exerciseId: number, patch: PrescriptionPatch) =>
  request<PlanLine>(`/cycle/templates/${templateId}/exercises/${exerciseId}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });

/** A movement the progression screen can chart, and the session it belongs to. */
export interface TrackedExercise {
  exerciseId: number;
  name: string;
  lastDate: string;
  days: number;
  templateId: number | null;
  /** Its place in that session's plan; null for a movement added on the day. */
  position: number | null;
}

/** The coach's three tabs, valid for the whole six week block. */
export interface CycleTemplate {
  id: number;
  tab: string;
  name: string;
  position: number;
  exercises: number;
  /** Already done in the cycle's current week. */
  doneThisWeek: boolean;
  lastPerformedOn: string | null;
  /** The muscles it targets, most sets first; the abs left out. */
  muscles: MuscleGroup[];
}

export interface Cycle {
  programId: number;
  name: string;
  startDate: string;
  weeks: number;
  currentWeek: number;
  /** Sessions done in that week, one done twice counting twice. */
  doneThisWeek: number;
  templates: CycleTemplate[];
}

/** Tick a line that has no load to log (the abs circuit). */
export const setExerciseDone = (sessionExerciseId: number, done: boolean) =>
  request<void>(`/session-exercises/${sessionExerciseId}/done`, { method: "PUT", body: JSON.stringify({ done }) });

/** Pass a movement over, the machine taken: not done, and said so. */
export const setExerciseSkipped = (sessionExerciseId: number, skipped: boolean) =>
  request<void>(`/session-exercises/${sessionExerciseId}/skipped`, {
    method: "PUT",
    body: JSON.stringify({ skipped }),
  });

/**
 * Take back a movement added mid-session. `sets` is the count the athlete
 * saw: the server deletes nothing if the line holds another. Not queued
 * offline, for the same reason as a cancelled session.
 */
export const removeSessionExercise = ({ sessionExerciseId, sets }: { sessionExerciseId: number; sets: number }) =>
  request<void>(`/session-exercises/${sessionExerciseId}?sets=${sets}`, { method: "DELETE" });

/** Close a session: from then on it counts for its week. */
export const finishSession = (sessionId: number) =>
  request<Session>(`/sessions/${sessionId}`, {
    method: "PATCH",
    body: JSON.stringify({ status: "done", finishedAt: new Date().toISOString() }),
  });

/** Undo a finish tapped too early. */
export const reopenSession = (sessionId: number) =>
  request<Session>(`/sessions/${sessionId}`, {
    method: "PATCH",
    body: JSON.stringify({ status: "in_progress", finishedAt: null }),
  });

/** Add a movement the coach did not prescribe to the current session. */
export const addExerciseToSession = (
  sessionId: number,
  name: string,
  target: { targetSets: number; targetReps: string; targetWeightKg: number | null },
) =>
  request<{ sessionExerciseId: number }>(`/sessions/${sessionId}/exercises`, {
    method: "POST",
    body: JSON.stringify({ name, ...target }),
  });

export interface Block {
  id: number | null;
  name: string;
  startDate: string;
  weeks: number;
  sessionsPerWeek: number;
  weekIndex: number;
  sessionsDone: number;
  sessionsPlanned: number;
  movers: { exerciseId: number; name: string; deltaKg: number; current: number }[];
  attendance: { week: string; done: number; planned: number }[];
}

export interface Overview {
  range: TrendRange;
  sessions: number;
  totalVolumeKg: number;
  totalReps: number;
  totalSets: number;
  volumeByWeek: { date: string; value: number }[];
  topExercises: { exerciseId: number; name: string; volumeKg: number; sets: number }[];
  /** The sessions themselves — including those the sheet logged, which carry no reps. */
  recentSessions: PerformedSession[];
}

/** One session of the cycle: a row per movement, a cell per done session. */
export interface ProgressGrid {
  template: { id: number; name: string };
  sessions: { id: number; date: string; week: number | null }[];
  rows: {
    exerciseId: number;
    name: string;
    method: string | null;
    /** Aligned with `sessions`; null when the movement was not done that day. */
    cells: ({
      source: "app" | "sheet";
      /** `reps` is null when the sheet is the only trace. */
      sets: { weightKg: number; dropWeightKg: number | null; reps: number | null }[];
    } | null)[];
  }[];
}

/** A session in the journal, with what it held: see the server's `listJournal`. */
export interface JournalEntry {
  id: number;
  date: string;
  name: string;
  status: "done" | "in_progress";
  finishedAt: string | null;
  templateId: number | null;
  perceivedEffort: number | null;
  withCoach: boolean;
  summary: string | null;
  exercises: number;
  sets: number;
  volumeKg: number | null;
  source: "app" | "sheet" | "mixed";
  muscles: { muscle: MuscleGroup; sets: number }[];
  /** Absent from a journal kept on the device before the wearable came. */
  wearable?: SessionWearable;
}

export const getJournal = (templateId?: number) =>
  request<{ sessions: JournalEntry[] }>(`/journal${templateId === undefined ? "" : `?template=${templateId}`}`);

export interface PerformedSession {
  id: number;
  date: string;
  name: string;
  status: "done" | "in_progress";
  exercises: number;
  sets: number;
  topWeightKg: number | null;
  /** "sheet" means loads only: no reps, so no volume and no 1RM. */
  source: "app" | "sheet" | "mixed";
}

export interface SyncStatus {
  configured: boolean;
  pushEnabled: boolean;
  lastPulledAt: string | null;
  lastPushedAt: string | null;
  lastError: string | null;
  pendingSets: number;
}

/** What an import of the coach's sheet read, tab by tab (server: `sheets/pull.ts`). */
export interface PullReport {
  applied: boolean;
  tabs: {
    tab: string;
    name: string;
    exercises: number;
    unmappedHeaders: string[];
    warnings: string[];
    error?: string;
  }[];
  history: {
    tab: string;
    found: boolean;
    rows: number;
    exercises: number;
    dates: number;
    skippedFromApp: number;
    sessionsCreated: number;
    warnings: string[];
    error?: string;
  } | null;
  /** The coach's measures tab ("Suivi"). */
  measures?: { tab: string; found: boolean; rows: number; warnings: string[]; error?: string };
}

/** What a send of the journal wrote back to the sheet (server: `sheets/push.ts`). */
export interface PushReport {
  history: { tab: string; appended: number; confirmed: number; missing: string[]; pushed: boolean };
  weights: { written: unknown[]; skipped: { exercise: string; reason: string }[]; pushed: boolean } | null;
}

// A minute: the sheet is read tab by tab over Google's API.
const SHEET_TIMEOUT_MS = 60_000;

/** Every weight taken, oldest first. */
export const getBodyMetrics = () => request<BodyMetric[]>("/metrics/body?range=all");

/** The day's body weight; typed again the same day, it replaces it. */
export const saveBodyWeight = (date: string, weightKg: number) =>
  request<void>("/metrics/body", { method: "POST", body: JSON.stringify({ date, weightKg }) });

/** The day's tape measure and scale figures; a figure left out keeps the day's. */
export const saveMeasures = (date: string, measures: Partial<Record<MeasureField, number>>) =>
  request<void>("/metrics/body", { method: "POST", body: JSON.stringify({ date, ...measures }) });

/* ---- insights ---------------------------------------------------- */

/** A line the server worked out for a screen: shown beside the sparkle. */
export interface Insight {
  kind: string;
  text: string;
}

/** "home", "start", "session/12", "exercise/80", "movement/4": what that screen has to say. */
export const getInsights = (scope: string) => request<{ insights: Insight[] }>(`/insights/${scope}`);

export interface CycleReport {
  title: string;
  items: { label: string; value: string; detail?: string }[];
}

/** The block's report, from its last two weeks on; `text` is the version to send the coach. */
export const getCycleReport = () => request<{ report: CycleReport | null; text: string | null }>("/insights/cycle");

/** Where Claude's app reaches the training: null without a public address. */
export const getConnector = () => request<{ url: string | null }>("/connector");

/* ---- whoop ------------------------------------------------------- */

/** The wearable's days over a range, inclusive: today's alone for the home screen. */
export const getWearableDays = (from: string, to: string) =>
  request<{ days: WearableDay[] }>(`/wearable/days?from=${from}&to=${to}`);

export interface WhoopStatus {
  /** The server has a Whoop app to sign in with. */
  configured: boolean;
  /** The whole history being read, after linking or when asked. */
  importing: boolean;
  account: {
    connectedAt: string;
    lastSyncedAt: string | null;
    lastError: string | null;
    records: Record<"cycle" | "recovery" | "sleep" | "workout" | "body", number>;
  } | null;
}

export const getWhoopStatus = () => request<WhoopStatus>("/whoop/status");
/** Where to send the browser to sign in on Whoop. */
export const connectWhoop = () => request<{ url: string }>("/whoop/connect", { method: "POST", body: "{}" });
/** The last days, read now: a handful of requests to Whoop, longer than the usual wait. */
export const syncWhoop = () => request<unknown>("/whoop/sync", { method: "POST", body: "{}" }, 30_000);
/** The whole history again, in the background on the server. */
export const reimportWhoop = () =>
  request<unknown>("/whoop/sync", { method: "POST", body: JSON.stringify({ history: true }) });
export const unlinkWhoop = () => request<void>("/whoop/connection", { method: "DELETE" });

/** Read the coach's sheet: a dry run unless `apply`. */
export const pullProgram = (apply: boolean) =>
  request<PullReport>("/sync/pull", { method: "POST", body: JSON.stringify({ apply }) }, SHEET_TIMEOUT_MS);

/** Send the journal to the sheet's history tab, then the loads to his Poids column. */
export const pushJournal = () => request<PushReport>("/sync/push", { method: "POST", body: "{}" }, SHEET_TIMEOUT_MS);

/* ---- writes: try the network, fall back to the queue --------------- */

/**
 * Saving a set never fails from the user's point of view. If the server is out
 * of reach the mutation lands in the queue and the UI shows it as pending; the
 * drain below replays it as soon as the server answers again.
 */
export async function saveSet(input: SetEntryInput): Promise<{ queued: boolean; set?: SetEntry }> {
  try {
    const saved = await request<SetEntry>("/sets", { method: "PUT", body: JSON.stringify(input) });
    return { queued: false, set: saved };
  } catch (error) {
    if (!canWait(error)) throw error;
    // Stamped now: the server would date it at the moment the queue drains.
    const payload = { ...input, completedAt: input.completedAt ?? new Date().toISOString() };
    await queue.enqueue({ kind: "set.upsert", clientId: input.clientId, payload });
    return { queued: true };
  }
}

export async function removeSet(clientId: string): Promise<{ queued: boolean }> {
  try {
    await request<void>(`/sets/${clientId}`, { method: "DELETE" });
    return { queued: false };
  } catch (error) {
    if (!canWait(error)) throw error;
    await queue.enqueue({ kind: "set.delete", clientId, payload: { clientId } });
    return { queued: true };
  }
}

/**
 * Cancel a session with everything in it. `sets` is the count the athlete saw
 * listed: the server deletes nothing if the session holds another. Not queued
 * offline, since that check only means something here and now.
 */
export const cancelSession = ({ sessionId, sets }: { sessionId: number; sets: number }) =>
  request<{ cancelled: boolean }>(`/sessions/${sessionId}?sets=${sets}`, { method: "DELETE" });

/**
 * Have the server write the session's summary, once its effort is scored.
 * Claude takes a few seconds, hence the long wait; nothing to show is no
 * error (null): a summary may be off on this server.
 */
export async function writeSessionSummary(sessionId: number): Promise<string | null> {
  const result = await request<{ summary: string } | undefined>(
    `/sessions/${sessionId}/summary`,
    { method: "POST" },
    120_000,
  );
  return result?.summary ?? null;
}

export async function patchSession(
  sessionId: number,
  patch: Partial<Pick<Session, "status" | "startedAt" | "finishedAt" | "perceivedEffort" | "notes">>,
): Promise<{ queued: boolean; session?: Session }> {
  try {
    const session = await request<Session>(`/sessions/${sessionId}`, { method: "PATCH", body: JSON.stringify(patch) });
    return { queued: false, session };
  } catch (error) {
    if (!canWait(error)) throw error;
    await queue.enqueue({
      kind: "session.patch",
      clientId: `session-${sessionId}`,
      payload: { sessionId, ...patch },
    });
    return { queued: true };
  }
}

/**
 * Done with the coach or alone, said by hand; null hands it back to the
 * sets. Said at the end of a session, still in the gym: queued like a set.
 */
export async function setSessionCoached(sessionId: number, withCoach: boolean | null): Promise<{ queued: boolean }> {
  try {
    await request<void>(`/sessions/${sessionId}/coach`, { method: "PUT", body: JSON.stringify({ withCoach }) });
    return { queued: false };
  } catch (error) {
    if (!canWait(error)) throw error;
    await queue.enqueue({
      kind: "session.coach",
      clientId: `session-coach-${sessionId}`,
      payload: { sessionId, withCoach },
    });
    return { queued: true };
  }
}

/**
 * The session's lines in a new order, every one of them. Moved in the gym,
 * a machine taken: queued like a set, the latest order wins.
 */
export async function reorderSession(sessionId: number, sessionExerciseIds: number[]): Promise<{ queued: boolean }> {
  try {
    await request<void>(`/sessions/${sessionId}/order`, {
      method: "PUT",
      body: JSON.stringify({ sessionExerciseIds }),
    });
    return { queued: false };
  } catch (error) {
    if (!canWait(error)) throw error;
    await queue.enqueue({
      kind: "session.order",
      clientId: `session-order-${sessionId}`,
      payload: { sessionId, sessionExerciseIds },
    });
    return { queued: true };
  }
}

/**
 * Machine setup typed between two sets, often where the signal is worst.
 * Queued like a set: one pending entry per movement, the latest value wins.
 */
export async function saveExerciseSettings(exerciseId: number, settings: string | null): Promise<{ queued: boolean }> {
  try {
    await request<Exercise>(`/exercises/${exerciseId}/settings`, {
      method: "PUT",
      body: JSON.stringify({ settings }),
    });
    return { queued: false };
  } catch (error) {
    if (!canWait(error)) throw error;
    await queue.enqueue({
      kind: "exercise.settings",
      clientId: `exercise-settings-${exerciseId}`,
      payload: { exerciseId, settings },
    });
    return { queued: true };
  }
}

/* ---- draining ------------------------------------------------------ */

let draining = false;

/**
 * Replay the queue in order. Stops at the first network failure so the
 * remaining items keep their order; a payload the server rejects outright (4xx
 * other than 401) is dropped, because replaying it forever would block
 * everything behind it.
 */
export async function drainQueue(): Promise<{ sent: number; remaining: number }> {
  if (draining) return { sent: 0, remaining: await queue.size() };
  draining = true;
  let sent = 0;

  try {
    const items = await queue.peek();
    for (const item of items) {
      try {
        if (item.kind === "set.upsert") {
          await request<SetEntry>("/sets", { method: "PUT", body: JSON.stringify(item.payload) });
        } else if (item.kind === "set.delete") {
          await request<void>(`/sets/${item.payload.clientId}`, { method: "DELETE" });
        } else if (item.kind === "exercise.settings") {
          const { exerciseId, settings } = item.payload;
          await request<Exercise>(`/exercises/${exerciseId}/settings`, {
            method: "PUT",
            body: JSON.stringify({ settings }),
          });
        } else if (item.kind === "session.coach") {
          const { sessionId, withCoach } = item.payload;
          await request<void>(`/sessions/${sessionId}/coach`, { method: "PUT", body: JSON.stringify({ withCoach }) });
        } else if (item.kind === "session.order") {
          const { sessionId, sessionExerciseIds } = item.payload;
          await request<void>(`/sessions/${sessionId}/order`, {
            method: "PUT",
            body: JSON.stringify({ sessionExerciseIds }),
          });
        } else {
          const { sessionId, ...patch } = item.payload;
          await request<Session>(`/sessions/${sessionId}`, { method: "PATCH", body: JSON.stringify(patch) });
        }
        await queue.remove([item.clientId]);
        sent++;
      } catch (error) {
        // Still offline, or signed out: try again later, after signing back in.
        if (canWait(error)) break;
        // The server refused the payload; keeping it would wedge the queue.
        console.warn("Dropping unsendable mutation", item, error);
        await queue.remove([item.clientId]);
      }
    }
  } finally {
    draining = false;
  }

  return { sent, remaining: await queue.size() };
}

export { queue };
